import React, { useState, useRef, useEffect } from 'react';
import { WarehouseIcon, ChevronDown, Check } from 'lucide-react';
import { useWarehouse } from '../../contexts/WarehouseContext';
import { useTranslation } from '../../contexts/I18nProvider';

/**
 * Dropdown selector for switching the active warehouse.
 * Shows "Alle lager" when no warehouse is selected (null = unfiltered).
 * Hidden when the tenant has only one warehouse.
 */
export const WarehouseIndicator: React.FC = () => {
  const { currentWarehouse, currentWarehouseName, warehouses, isLoading, isWarehouseLocked, setCurrentWarehouse } = useWarehouse();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Hide until warehouse list is loaded
  if (warehouses.length === 0) {
    return null;
  }

  const isSingleWarehouse = warehouses.length <= 1;

  // Resolve name from warehouses list directly (avoids stale currentWarehouseName before fetch completes)
  const resolvedName = currentWarehouse
    ? (warehouses.find(w => w.warehouseID === currentWarehouse)?.warehouseDescr || currentWarehouseName || currentWarehouse)
    : null;

  const displayLabel = currentWarehouse
    ? resolvedName
    : isSingleWarehouse
      ? (warehouses[0]?.warehouseDescr || warehouses[0]?.warehouseID || '')
      : t('warehouse.selector.allWarehouses');

  const handleSelect = (warehouseId: string | null) => {
    setCurrentWarehouse(warehouseId);
    setIsOpen(false);
  };

  // Single warehouse — or profile locked to its default warehouse (TW 36430604): read-only badge
  if (isSingleWarehouse || isWarehouseLocked) {
    return (
      <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200">
        <WarehouseIcon size={14} />
        <span className="whitespace-nowrap max-w-[120px] truncate">{displayLabel}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative hidden sm:block">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200 hover:bg-blue-100 transition-colors cursor-pointer"
        title={t('warehouse.selector.selectWarehouse')}
      >
        <WarehouseIcon size={14} />
        <span className="whitespace-nowrap max-w-[120px] truncate">{displayLabel}</span>
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          {/* "Alle lager" option */}
          <button
            onClick={() => handleSelect(null)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-blue-50 transition-colors text-left"
          >
            <Check size={14} className={currentWarehouse === null ? 'text-blue-600' : 'invisible'} />
            <span className={currentWarehouse === null ? 'font-semibold text-blue-700' : 'text-gray-700'}>
              {t('warehouse.selector.allWarehouses')}
            </span>
          </button>

          <div className="border-t border-gray-100 my-1" />

          {/* Warehouse list */}
          {warehouses.map(w => {
            const isActive = currentWarehouse === w.warehouseID;
            return (
              <button
                key={w.warehouseID}
                onClick={() => handleSelect(w.warehouseID)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-blue-50 transition-colors text-left"
              >
                <Check size={14} className={isActive ? 'text-blue-600' : 'invisible'} />
                <span className={isActive ? 'font-semibold text-blue-700' : 'text-gray-700'}>
                  {w.warehouseDescr || w.warehouseID}
                </span>
                <span className="text-gray-400 ml-auto">{w.warehouseID}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
